import { useEffect, useRef, useState } from "react";

// Parallax scroll-story: alternating full-bleed photos + single-line text frames.
// The photo translates upward slower than the page scroll; text reveals as the
// panel enters view. Intentionally slow, heavy negative space.
export default function ParallaxStory({ panels }) {
  return (
    <div data-testid="guatemala-parallax-story">
      {panels.map((p, i) => (
        <ParallaxPanel key={i} panel={p} index={i} />
      ))}
    </div>
  );
}

function ParallaxPanel({ panel, index }) {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setVisible(true));
      },
      { threshold: 0.25 }
    );
    io.observe(el);

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        // progress from -1 (panel below) to 1 (panel above)
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        if (imgRef.current) {
          // Translate image slower than scroll — classic parallax
          imgRef.current.style.transform = `translate3d(0, ${progress * -40}px, 0) scale(1.08)`;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  const align = panel.align || (index % 2 === 0 ? "left" : "right");

  return (
    <section
      ref={ref}
      className="relative h-screen min-h-[720px] overflow-hidden bg-black"
      data-testid={`guatemala-panel-${index}`}
    >
      <div
        ref={imgRef}
        className="absolute inset-0 will-change-transform"
        style={{ transform: "translate3d(0,0,0) scale(1.08)" }}
      >
        <img
          src={panel.image}
          alt=""
          className="wc-image absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 gt-grain pointer-events-none opacity-80" />
      </div>

      <div
        className={`relative z-10 h-full flex flex-col px-6 md:px-16 ${
          align === "right"
            ? "items-end text-right"
            : align === "center"
              ? "items-center text-center"
              : "items-start text-left"
        } justify-end pb-24 md:pb-28`}
      >
        <div className="max-w-2xl">
          {panel.label && (
            <div
              className={`wc-label text-[10px] md:text-xs gt-red tracking-[0.4em] mb-5 transition-all duration-[900ms] ease-out ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
              }`}
            >
              {panel.label}
            </div>
          )}
          <h3
            className={`wc-heading text-white text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-widest transition-all duration-[900ms] ease-out ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: "120ms" }}
          >
            {panel.heading}
          </h3>
          {panel.body && (
            <p
              className={`text-white/80 text-base md:text-lg mt-6 leading-[1.9] transition-all duration-[900ms] ease-out ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "240ms" }}
            >
              {panel.body}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
